"use client";

import { useTailwind } from "@oreme/ui";
import type { FeatureCollection } from "geojson";
import { useSetAtom } from "jotai";
import type { MapLayerMouseEvent } from "maplibre-gl";
import { RLayer, RSource, useMap } from "maplibre-react-components";
import React, { useMemo, type ReactElement } from "react";
import { activeStationAtom } from "@/store/weather";
import type { WeatherStation } from "@/types";

interface WeatherStationLayerProps {
  features: FeatureCollection;
}

function WeatherStationLayer({
  features,
}: Readonly<WeatherStationLayerProps>): ReactElement {
  const map = useMap();
  const tailwind = useTailwind();
  const setActiveStation = useSetAtom(activeStationAtom);

  const sourceId = `source-weather`;
  const layerId = `layer-weather`;
  const paint = useMemo(
    () => ({
      default: {
        "circle-stroke-width": 1,
        "circle-radius": 5,
        "circle-stroke-color": tailwind.theme.colors.blue[700],
        "circle-color": tailwind.theme.colors.blue[500],
      },
      active: {
        "circle-stroke-width": 1,
        "circle-radius": 5,
        "circle-stroke-color": tailwind.theme.colors.blue[900],
        "circle-color": tailwind.theme.colors.blue[700],
      },
    }),
    [tailwind],
  );

  const handleClick = (e: MapLayerMouseEvent): void => {
    if (e.features?.[0].properties) {
      const newStation = e.features[0].properties as WeatherStation;
      setActiveStation(newStation);
    }
  };

  const handleMouseEnter = (): void => {
    map.getCanvas().style.cursor = "pointer";
  };

  const handleMouseLeave = (): void => {
    map.getCanvas().style.cursor = "";
  };

  return (
    <>
      <RSource
        data={features}
        generateId
        id={sourceId}
        key={sourceId}
        type="geojson"
      />
      <RLayer
        id={layerId}
        key={layerId}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        paint={paint.default}
        source={sourceId}
        type="circle"
      />
    </>
  );
}

export { WeatherStationLayer, type WeatherStationLayerProps };
